/**
 * User Roster tab on the SuperAdmin Platform page.
 *
 * One row per platform user, lifetime stats sourced from each user's most
 * recent analytics-daily row (cumulatives stamped by the aggregator).
 * No date selection. Includes users with `status != "active"` and users
 * who have never traded — operator sees the full platform population.
 *
 * Backed by GET /analytics/user-roster (SuperAdmin only).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Download,
  Eye,
  RefreshCw,
  Search,
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useActingAs } from '../../context/ActingAsContext';
import platformPnlService from '../../services/platformPnlService';
import { downloadFile } from '../../utils/downloadFile';
import { cn } from '../../utils/cn';
import { formatInr, formatPct, istCalendarToday, pnlClass } from '../../utils/platformFormat';
import type { UserRosterRow } from '../../types/analytics';

// ─── columns ────────────────────────────────────────────────────────────

type SortKey =
  | 'user'
  | 'lifetime_net_pnl'
  | 'lifetime_trades_total'
  | 'lifetime_win_rate'
  | 'lifetime_roi_pct'
  | 'capital_deployed'
  | 'last_trade_date'
  | 'created_at';
type SortDir = 'asc' | 'desc';

const COLUMNS: Array<{
  key: SortKey;
  label: string;
  align?: 'left' | 'right';
}> = [
  { key: 'user', label: 'User', align: 'left' },
  { key: 'lifetime_net_pnl', label: 'Lifetime P&L', align: 'right' },
  { key: 'lifetime_trades_total', label: 'Total Trades', align: 'right' },
  { key: 'lifetime_win_rate', label: 'Win %', align: 'right' },
  { key: 'lifetime_roi_pct', label: 'ROI %', align: 'right' },
  { key: 'capital_deployed', label: 'Capital', align: 'right' },
  { key: 'last_trade_date', label: 'Last Trade', align: 'right' },
  { key: 'created_at', label: 'Joined', align: 'right' },
];

// ─── filters ────────────────────────────────────────────────────────────

type Bucket = 'all' | 'profit' | 'loss' | 'breakeven' | 'never_traded';
type StatusFilter = 'all' | 'active' | 'inactive';

interface FilterState {
  search: string;
  bucket: Bucket;
  status: StatusFilter;
  tradingEnabled: 'all' | 'on' | 'off';
  hideNeverTraded: boolean;
  brokers: Set<string>;
  accountManagers: Set<string>;
  clientGroups: Set<string>;
}

const EMPTY_FILTERS: FilterState = {
  search: '',
  bucket: 'all',
  status: 'all',
  tradingEnabled: 'all',
  hideNeverTraded: false,
  brokers: new Set(),
  accountManagers: new Set(),
  clientGroups: new Set(),
};

// ─── helpers ────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Backend returns YYYY-MM-DD (last_trade_date) OR full ISO (created_at).
  // Normalize to short DD MMM YYYY.
  try {
    const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00+05:30`);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return iso;
  }
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  try {
    const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00+05:30`);
    const today = new Date(`${istCalendarToday()}T00:00:00+05:30`);
    return Math.floor((today.getTime() - d.getTime()) / 86_400_000);
  } catch {
    return null;
  }
}

// ─── tab ────────────────────────────────────────────────────────────────

interface UserRosterTabProps {
  registerActions: (node: React.ReactNode) => void;
}

const UserRosterTab: React.FC<UserRosterTabProps> = ({ registerActions }) => {
  const { setActingAs } = useActingAs();
  const navigate = useNavigate();

  const [rows, setRows] = useState<UserRosterRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('lifetime_net_pnl');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchPage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await platformPnlService.getUserRoster();
      setRows(resp.rows || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load user roster');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // ── derived: filter options ──
  const brokerOptions = useMemo(() => {
    const out = new Set<string>();
    for (const r of rows) for (const a of r.accounts) if (a.broker) out.add(a.broker);
    return Array.from(out).sort();
  }, [rows]);

  const amOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      const am = r.account_manager;
      if (am) map.set(am.user_id, am.full_name || am.email || am.user_id);
    }
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const groupOptions = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    for (const r of rows) {
      const g = r.client_group;
      if (g) map.set(g.group_id, { name: g.name, color: g.color });
    }
    return Array.from(map.entries())
      .map(([id, val]) => ({ id, ...val }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (needle) {
        const haystack = `${r.full_name} ${r.email} ${r.user_id} ${r.zebu_client_id} ${r.accounts.map((a) => a.client_id).join(' ')}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (filters.bucket === 'profit' && r.lifetime_net_pnl <= 0) return false;
      if (filters.bucket === 'loss' && r.lifetime_net_pnl >= 0) return false;
      if (filters.bucket === 'breakeven' && r.lifetime_net_pnl !== 0) return false;
      if (filters.bucket === 'never_traded' && r.has_traded) return false;
      if (filters.hideNeverTraded && !r.has_traded) return false;
      if (filters.status === 'active' && r.status !== 'active') return false;
      if (filters.status === 'inactive' && r.status === 'active') return false;
      if (filters.tradingEnabled === 'on' && !r.trading_enabled) return false;
      if (filters.tradingEnabled === 'off' && r.trading_enabled) return false;
      if (filters.brokers.size > 0) {
        if (!r.accounts.some((a) => filters.brokers.has(a.broker))) return false;
      }
      if (filters.accountManagers.size > 0) {
        const amId = r.account_manager?.user_id;
        if (!amId || !filters.accountManagers.has(amId)) return false;
      }
      if (filters.clientGroups.size > 0) {
        const gid = r.client_group?.group_id;
        if (!gid || !filters.clientGroups.has(gid)) return false;
      }
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      if (sortKey === 'user') {
        return (a.full_name || a.email || a.user_id)
          .localeCompare(b.full_name || b.email || b.user_id) * dir;
      }
      if (sortKey === 'last_trade_date' || sortKey === 'created_at') {
        const av = a[sortKey] || '';
        const bv = b[sortKey] || '';
        if (av === bv) return 0;
        return (av < bv ? -1 : 1) * dir;
      }
      const av = (a[sortKey] as number | null) ?? Number.NEGATIVE_INFINITY;
      const bv = (b[sortKey] as number | null) ?? Number.NEGATIVE_INFINITY;
      if (av === bv) return 0;
      return av < bv ? -1 * dir : 1 * dir;
    });
    return out;
  }, [rows, filters, sortKey, sortDir]);

  const kpis = useMemo(() => {
    const sumLifetime = visibleRows.reduce((s, r) => s + r.lifetime_net_pnl, 0);
    const sumCap = visibleRows.reduce((s, r) => s + r.capital_deployed, 0);
    const sumTrades = visibleRows.reduce((s, r) => s + r.lifetime_trades_total, 0);
    const tradedEver = visibleRows.filter((r) => r.has_traded).length;
    const profitable = visibleRows.filter((r) => r.lifetime_net_pnl > 0).length;
    const lossMaking = visibleRows.filter((r) => r.lifetime_net_pnl < 0).length;
    const neverTraded = visibleRows.filter((r) => !r.has_traded).length;
    return {
      total: visibleRows.length,
      sumLifetime,
      sumCap,
      sumTrades,
      tradedEver,
      profitable,
      lossMaking,
      neverTraded,
    };
  }, [visibleRows]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir((key === 'user' || key === 'created_at') ? 'asc' : 'desc');
    }
  };

  const toggleSetFilter = (
    field: 'brokers' | 'accountManagers' | 'clientGroups',
    value: string,
  ) => {
    setFilters((f) => {
      const next = new Set(f[field]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...f, [field]: next };
    });
  };

  const onExportCsv = useCallback(() => {
    const header = [
      'user_id', 'full_name', 'email', 'status', 'trading_enabled',
      'created_at', 'account_manager', 'client_group', 'brokers',
      'capital_deployed', 'lifetime_pnl', 'lifetime_trades',
      'lifetime_wins', 'lifetime_losses', 'lifetime_win_rate',
      'lifetime_roi_pct', 'last_trade_date', 'days_since_last_trade',
    ];
    const escape = (v: any): string => {
      let s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (s && /^[=+\-@]/.test(s)) s = `'${s}`;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      header.join(','),
      ...visibleRows.map((r) => [
        r.user_id, r.full_name, r.email, r.status, r.trading_enabled,
        r.created_at,
        r.account_manager?.full_name || r.account_manager?.email || '',
        r.client_group?.name || '',
        r.accounts.map((a) => `${a.broker}:${a.client_id}`).join('|'),
        r.capital_deployed, r.lifetime_net_pnl, r.lifetime_trades_total,
        r.lifetime_trades_won, r.lifetime_trades_lost, r.lifetime_win_rate,
        r.lifetime_roi_pct ?? '', r.last_trade_date || '',
        daysSince(r.last_trade_date) ?? '',
      ].map(escape).join(',')),
    ];
    downloadFile(`user-roster-${istCalendarToday()}.csv`, lines.join('\n'), 'text/csv;charset=utf-8');
  }, [visibleRows]);

  // Wire tab actions into PageHeader.
  useEffect(() => {
    registerActions(
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={fetchPage}
          leftIcon={<RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportCsv}
          leftIcon={<Download className="h-4 w-4" />}
          disabled={visibleRows.length === 0}
        >
          Export CSV
        </Button>
      </div>
    );
  }, [registerActions, fetchPage, isLoading, onExportCsv, visibleRows.length]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <Kpi label="Users" value={String(kpis.total)} tone="neutral" />
        <Kpi label="Lifetime P&L" value={formatInr(kpis.sumLifetime)} tone={kpis.sumLifetime >= 0 ? 'pos' : 'neg'} />
        <Kpi label="Capital" value={formatInr(kpis.sumCap)} tone="neutral" />
        <Kpi label="Total Trades" value={String(kpis.sumTrades)} tone="neutral" />
        <Kpi label="Traded ever" value={String(kpis.tradedEver)} tone="neutral" />
        <Kpi label="Profitable" value={String(kpis.profitable)} tone="pos" />
        <Kpi label="Loss-making" value={String(kpis.lossMaking)} tone="neg" />
        <Kpi label="Never traded" value={String(kpis.neverTraded)} tone="neutral" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users ({visibleRows.length} of {rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Filter bar */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="Search name, email, user_id, client ID…"
                leftIcon={<Search className="h-4 w-4" />}
                size="sm"
                className="w-72"
              />

              <select
                value={filters.bucket}
                onChange={(e) => setFilters((f) => ({ ...f, bucket: e.target.value as Bucket }))}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
              >
                <option value="all">All P&L</option>
                <option value="profit">Profitable lifetime</option>
                <option value="loss">Loss-making lifetime</option>
                <option value="breakeven">Breakeven</option>
                <option value="never_traded">Never traded</option>
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as StatusFilter }))}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
              >
                <option value="all">Any status</option>
                <option value="active">Active only</option>
                <option value="inactive">Non-active only</option>
              </select>

              <select
                value={filters.tradingEnabled}
                onChange={(e) => setFilters((f) => ({ ...f, tradingEnabled: e.target.value as 'all' | 'on' | 'off' }))}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
              >
                <option value="all">Trading: any</option>
                <option value="on">Trading: enabled</option>
                <option value="off">Trading: disabled</option>
              </select>

              <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={filters.hideNeverTraded}
                  onChange={(e) => setFilters((f) => ({ ...f, hideNeverTraded: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Hide never-traded
              </label>

              <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            </div>

            {brokerOptions.length > 0 && (
              <ChipMultiSelect
                label="Broker"
                options={brokerOptions.map((b) => ({ id: b, label: b.toUpperCase() }))}
                selected={filters.brokers}
                onToggle={(v) => toggleSetFilter('brokers', v)}
              />
            )}
            {amOptions.length > 0 && (
              <ChipMultiSelect
                label="Account Manager"
                options={amOptions}
                selected={filters.accountManagers}
                onToggle={(v) => toggleSetFilter('accountManagers', v)}
              />
            )}
            {groupOptions.length > 0 && (
              <ChipMultiSelect
                label="Client Group"
                options={groupOptions.map((g) => ({ id: g.id, label: g.name, dot: g.color || undefined }))}
                selected={filters.clientGroups}
                onToggle={(v) => toggleSetFilter('clientGroups', v)}
              />
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading…</span>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              {rows.length === 0
                ? 'No users yet.'
                : 'No users match the current filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        className={cn(
                          'px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-100',
                          col.align === 'right' ? 'text-right' : 'text-left',
                        )}
                        onClick={() => toggleSort(col.key)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {sortKey === col.key && (
                            <ChevronDown
                              className={cn('h-3 w-3 transition-transform', sortDir === 'asc' && 'rotate-180')}
                            />
                          )}
                        </span>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {visibleRows.map((r) => {
                    const last = daysSince(r.last_trade_date);
                    return (
                      <tr key={r.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {r.full_name || '(unnamed)'}
                            {r.status && r.status !== 'active' && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase">
                                {r.status}
                              </span>
                            )}
                            {!r.trading_enabled && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-200 uppercase">
                                Trading off
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{r.email || r.user_id}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {r.accounts.map((a) => (
                              <span
                                key={`${a.broker}:${a.client_id}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase"
                              >
                                <span
                                  className={cn('inline-block h-1.5 w-1.5 rounded-full',
                                    a.auto_login_enabled ? 'bg-green-500' : 'bg-gray-400')}
                                />
                                {a.broker}:{a.client_id}
                              </span>
                            ))}
                            {r.account_manager && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                AM: {r.account_manager.full_name || r.account_manager.email}
                              </span>
                            )}
                            {r.client_group && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                <span
                                  className="inline-block h-2 w-2 rounded-full"
                                  style={{ backgroundColor: r.client_group.color || '#9ca3af' }}
                                />
                                {r.client_group.name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(r.lifetime_net_pnl))}>
                          {r.has_traded ? formatInr(r.lifetime_net_pnl) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                          {r.has_traded ? (
                            <>
                              <span className="font-medium">{r.lifetime_trades_total}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                ({r.lifetime_trades_won}W/{r.lifetime_trades_lost}L)
                              </span>
                            </>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                          {r.has_traded ? formatPct(r.lifetime_win_rate, 1) : '—'}
                        </td>
                        <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(r.lifetime_roi_pct ?? 0))}>
                          {formatPct(r.lifetime_roi_pct)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                          {formatInr(r.capital_deployed)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                          {r.last_trade_date ? (
                            <>
                              <div>{formatDate(r.last_trade_date)}</div>
                              {last != null && (
                                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                  {last === 0 ? 'today' : `${last}d ago`}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">Never</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                          {formatDate(r.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              // Plumb the user's display label so the header
                              // chip shows a name instead of a UUID — SA
                              // targets here aren't in the managed pool.
                              setActingAs(
                                r.user_id,
                                r.full_name || r.email || r.user_id,
                              );
                              navigate('/others/analytics');
                            }}
                            leftIcon={<Eye className="h-3.5 w-3.5" />}
                            title="View this user's analytics"
                          >
                            View as
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── child components (shared shape with DailyPnlTab; intentional duplication
//     to keep each tab self-contained — these are small enough that an extra
//     util file would be more friction than re-declaration) ──

interface KpiProps {
  label: string;
  value: string;
  tone: 'pos' | 'neg' | 'neutral';
}

const Kpi: React.FC<KpiProps> = ({ label, value, tone }) => {
  const toneClass = tone === 'pos'
    ? 'text-green-600 dark:text-green-400'
    : tone === 'neg'
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className={cn('text-xl font-bold mt-1 tabular-nums', toneClass)}>{value}</div>
    </div>
  );
};

interface ChipMultiSelectProps {
  label: string;
  options: { id: string; label: string; dot?: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}

const ChipMultiSelect: React.FC<ChipMultiSelectProps> = ({ label, options, selected, onToggle }) => (
  <div className="flex items-start gap-2 flex-wrap">
    <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-1.5 shrink-0">
      {label}
    </span>
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.has(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors',
              active
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
            )}
          >
            {opt.dot && (
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: opt.dot }}
              />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default UserRosterTab;
